import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { JwtService } from '@core/services/jwt.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@core/services/language.service';
import { SiteService } from '@data-access/services/site.service';
import { Site } from '@core/models/site.model';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <header class="bg-white shadow-sm border-b border-gray-200 fixed top-0 right-0 left-0 lg:left-64 z-30">
      <div class="pl-14 pr-3 sm:px-6 py-3 sm:py-4 lg:pl-6">
        <div class="flex items-center justify-between">
          <!-- Breadcrumb -->
          <div class="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
            <nav class="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm overflow-hidden ml-8 sm:ml-0">
              <a href="#" class="text-gray-500 hover:text-gray-700 whitespace-nowrap hidden sm:inline">{{ 'HEADER.BRAND' | translate }}</a>
              <svg class="w-3 h-3 sm:w-4 sm:h-4 text-gray-300 flex-shrink-0 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
              <span class="text-gray-900 font-medium truncate">{{ currentPageTitle }}</span>
            </nav>
          </div>

          <!-- Actions -->
          <div class="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <!-- Store Selector (always show current site, dropdown only if multiple sites) -->
            <div *ngIf="currentSite" class="relative inline-block text-left">
              <!-- Button/Display - clickable only if multiple sites -->
              <button
                *ngIf="sites.length > 1; else singleSite"
                type="button"
                (click)="toggleSiteDropdown()"
                class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
                <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
                <span class="truncate max-w-[120px]">
                  {{ currentSite.label }}
                </span>
                <svg class="w-4 h-4 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              <!-- Single site - non-clickable display -->
              <ng-template #singleSite>
                <div class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  <span class="truncate max-w-[120px]">
                    {{ currentSite.label }}
                  </span>
                </div>
              </ng-template>

              <!-- Dropdown Menu (only if multiple sites) -->
              <div *ngIf="isSiteDropdownOpen && sites.length > 1"
                   class="absolute right-0 z-50 w-64 mt-2 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                <div class="py-1">
                  <button *ngFor="let site of sites"
                          (click)="selectSiteFromDropdown(site)"
                          [class.bg-purple-50]="site.identifier === currentSite.identifier"
                          class="flex items-start w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <svg *ngIf="site.identifier === currentSite.identifier"
                         class="w-5 h-5 mr-2 text-purple-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                    <div class="flex-1 text-left">
                      <div class="font-medium">{{ site.label }}</div>
                      <div class="text-xs text-gray-500">{{ site.address }}</div>
                    </div>
                  </button>
                </div>
              </div>

              <!-- Overlay -->
              <div *ngIf="isSiteDropdownOpen"
                   (click)="closeSiteDropdown()"
                   class="fixed inset-0 z-40"></div>
            </div>

            <!-- Language Selector -->
            <select (change)="switchLang($any($event.target).value)" [value]="currentLang" class="border rounded px-1.5 sm:px-2 py-1 text-xs sm:text-sm">
              <option value="fr">FR</option>
              <option value="en">EN</option>
            </select>
          </div>
        </div>
      </div>
    </header>
  `
})
export class HeaderComponent implements OnInit, OnDestroy {
  currentPageTitle = '';
  private tokenSubscription: Subscription | null = null;
  private langChangeSubscription: Subscription | null = null;
  currentLang = 'fr';

  // Site selector properties
  currentSite: Site | null = null;
  sites: Site[] = [];
  isSiteDropdownOpen = false;

  user = {
    name: '',
    organisation: '',
    role: ''
  };
  constructor(
    private router: Router,
    private jwtService: JwtService,
    public translate: TranslateService,
    private languageService: LanguageService,
    private siteService: SiteService
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updatePageTitle(event.url);
      });
  }

  ngOnInit(): void {
    this.updatePageTitle(this.router.url);

    // Subscribe to selected site
    this.siteService.selectedSite$.subscribe(site => {
      this.currentSite = site;
    });

    // Subscribe to sites list
    this.siteService.sites$.subscribe(sites => {
      this.sites = sites;
    });

    // Load sites
    this.siteService.loadSites();

    // S'abonner aux changements de langue
    this.languageService.currentLanguage$.subscribe(lang => {
      this.currentLang = lang;
      this.updatePageTitle(this.router.url);
    });

    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.updatePageTitle(this.router.url);
    });

    this.tokenSubscription = this.jwtService.token$.subscribe(decodedToken => {
      try {
        if (decodedToken && decodedToken.name && decodedToken.sub) {
          // Utilise le nom complet du token s'il existe
          this.user.name = decodedToken.name;
          const match = decodedToken.sub.match(/@([^.]+)\./);
          const organisation = match ? match[1] : "";
          this.user.organisation = decodedToken.organization ?? organisation.toUpperCase();
          if (this.user.organisation === "WARIFLOW" || this.user.organisation === "SPEEDPRESTA") {
            // call admin
          }
        } else {
          // Si pas de token, réinitialiser le nom
          this.user.name = '';
          this.user.organisation = '';
        }
      } catch (error) {
        console.error('Error processing token in header component:', error);
        this.user.name = '';
        this.user.organisation = '';
      }
    });
  }

  ngOnDestroy(): void {
    this.tokenSubscription?.unsubscribe();
    this.langChangeSubscription?.unsubscribe();
  }

  private updatePageTitle(url: string) {
    const titleMap: { [key: string]: string } = {
      '/dashboard': 'HEADER.DASHBOARD',
      '/planning/status': 'HEADER.PLANNING',
      '/planning/replenishment': 'HEADER.PLANNING',
      '/planning/rules': 'HEADER.PLANNING',
      '/products/detail': 'HEADER.PRODUCTS',
      '/invoices': 'HEADER.INVOICES',
      '/quotes': 'HEADER.QUOTES',
      '/customers': 'HEADER.CUSTOMERS',
      '/settings': 'HEADER.SETTINGS'
    };

    let key = 'HEADER.DASHBOARD'; // Default key
    for (const path in titleMap) {
        if (url.startsWith(path)) {
            key = titleMap[path];
            break;
        }
    }

    this.translate.get(key).subscribe(translation => {
      this.currentPageTitle = translation;
    });
  }

  switchLang(lang: string) {
    // Utiliser le LanguageService pour synchroniser avec localStorage et le paramètre URL
    this.languageService.setLanguage(lang);
  }

  toggleSiteDropdown() {
    this.isSiteDropdownOpen = !this.isSiteDropdownOpen;
  }

  closeSiteDropdown() {
    this.isSiteDropdownOpen = false;
  }

  selectSiteFromDropdown(site: Site) {
    this.siteService.selectSite(site);
    this.closeSiteDropdown();
  }

  getActualOrganizationByJwt(): string {
    return this.jwtService.decodeToken()?.organization ?? "";
  }
}